/**
 * Y-axis scale helper for the recent-videos chart.
 *
 * Rounds the data maximum up to two significant figures, so the top of the axis
 * sits just above the tallest bar.
 * Two figures is what keeps the label readable once `formatAvv` has compacted
 * it - 810M, 1.2M, 47K - while staying close enough to the data to be honest.
 */

/** Significant figures kept in the axis maximum. */
const PRECISION = 2;

/**
 * Smallest two-significant-figure value at or above `rawMax`.
 *
 * Views are whole numbers, so the result is floored at 1 - that also covers the
 * chart's own `max = 1` floor and any zero, negative or non-finite input.
 */
export function niceMax(rawMax: number): number {
  const safe = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : 1;

  // Scale so the first two significant digits sit above the decimal point,
  // round up, then scale back.
  const magnitude = 10 ** (Math.floor(Math.log10(safe)) - PRECISION + 1);
  // Slack absorbs float drift, so a value already at two figures is not pushed
  // up to the next one - 1_200_000 / 10_000 can land on 120.00000000000001.
  const rounded = Math.ceil(safe / magnitude - 1e-9) * magnitude;

  // Ceiling rather than rounding: views can be fractional (projected views are
  // a percentage uplift), and rounding 1.4 down to 1 would put the axis below
  // the data and overflow the tallest bar.
  return Math.max(Math.ceil(rounded), Math.ceil(safe), 1);
}
